import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getCalibrationProcedures,
  getCalibrationProcedure,
  createCalibrationProcedure,
  updateCalibrationProcedure,
  deleteCalibrationProcedure,
} from '../api';
import type {
  CalibrationProcedureFilters,
  CreateCalibrationProcedureInput,
  UpdateCalibrationProcedureInput,
} from '../types';

const CDC_REFRESH_DELAYS_MS = [1500, 5000, 9000] as const;

// =============================================================================
// Query Keys Factory
// =============================================================================

export const calibrationProcedureKeys = {
  all: ['calibration-procedures'] as const,
  lists: () => [...calibrationProcedureKeys.all, 'list'] as const,
  list: (filters?: CalibrationProcedureFilters) =>
    [...calibrationProcedureKeys.lists(), filters] as const,
  detail: (id: string) => [...calibrationProcedureKeys.all, 'detail', id] as const,
};

function refreshCalibrationProcedureQueries(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: calibrationProcedureKeys.all });

  for (const delay of CDC_REFRESH_DELAYS_MS) {
    window.setTimeout(() => {
      void queryClient.refetchQueries({
        queryKey: calibrationProcedureKeys.all,
        type: 'active',
      });
    }, delay);
  }
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch calibration procedures list with filtering and pagination
 */
export function useCalibrationProceduresList(filters: CalibrationProcedureFilters = {}) {
  return useQuery({
    queryKey: calibrationProcedureKeys.list(filters),
    queryFn: () => getCalibrationProcedures(filters),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch a single calibration procedure by ID
 */
export function useCalibrationProcedure(id: string) {
  return useQuery({
    queryKey: calibrationProcedureKeys.detail(id),
    queryFn: () => getCalibrationProcedure(id),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

export function useCreateCalibrationProcedure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCalibrationProcedureInput) => createCalibrationProcedure(data),
    onSuccess: () => {
      refreshCalibrationProcedureQueries(queryClient);
      toast.success('Calibration procedure created');
    },
    onError: (error) => {
      toast.error('Failed to create calibration procedure', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

export function useUpdateCalibrationProcedure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateCalibrationProcedureInput;
    }) => updateCalibrationProcedure(id, data),
    onSuccess: () => {
      refreshCalibrationProcedureQueries(queryClient);
      toast.success('Calibration procedure updated');
    },
    onError: (error) => {
      toast.error('Failed to update calibration procedure', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

export function useDeleteCalibrationProcedure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCalibrationProcedure(id),
    onSuccess: () => {
      refreshCalibrationProcedureQueries(queryClient);
      toast.success('Calibration procedure deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete calibration procedure', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}
